import React, { useContext, useMemo } from 'react';
import { getAttribute } from '../../../../../../../../../Model/avenyModelUtils';
import { ModelContext } from '../../../../../../../../../Model/ModelContext/ModelContext';
import BokforderContext from '../../../BokforderContext/BokforderContext';
import EditableAttribute from '../../../../../../../../../Components/Attributes/EditableAttribute/EditableAttribute';
import EDIconButton from '../../../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import { BokforderType__Associations_Meddelande_All } from '../../../../Queries/_QueryTypes/BokforderType';
import {
  BokforderQuery_node_TEDEKBokforder,
  BokforderQuery_node_TEDEKBokforder__Associations_Rader,
} from '../../../../Queries/_QueryTypes/BokforderQuery';
import { useBusinessDataFormContext } from '../../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import EDBox from '../../../../../../../../../BaseComponents/EDBox/EDBox';
import AvenyTheme from '../../../../../../../../../BaseComponents/ThemeProvider/AvenyTheme';
import {
  isUserReservForFranvaro,
  getMessageLabel,
} from '../../Utils/BokforderMessagesUtils';

interface IProps {
  deleteOrUndoMessage: (
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => void;
  deleteOrUndoMessageTooltip: string;
  hasChanged: boolean;
  hasMessageProp: boolean;
  index: number;
  isEditMode: boolean;
  message: BokforderType__Associations_Meddelande_All;
  setOriginalValue: React.Dispatch<React.SetStateAction<string>>;
  showEditButtons: boolean;
}

const BokforderMessage: React.FC<React.PropsWithChildren<IProps>> = props => {
  const formContext =
    useBusinessDataFormContext<BokforderQuery_node_TEDEKBokforder>();
  const bokforderContext = useContext(BokforderContext);
  const modelContext = useContext(ModelContext);

  const bokforderMessageModel = modelContext.avenyCollectionMap.get(
    'ekbokfordermeddelande'
  );

  const bokforderMessageAttribute = getAttribute(
    'EKBokforderMeddelande',
    ' ',
    'MEDDELANDE',
    modelContext.avenyCollectionMap
  );

  if (!bokforderMessageAttribute || !bokforderMessageModel) {
    throw new Error(
      'Kan inte hitta Bokföringsorder meddelandens attribut/model-information'
    );
  }

  const isMessageSentByAttestant =
    props.message._Associations?.Attestant != null;

  // only check if message has been sent by an attestant otherwise it shouldnt be a reserv
  const attestantSender = useMemo(
    () =>
      isMessageSentByAttestant
        ? formContext?.data?._Associations?.Rader?.find(
            row =>
              row._Associations?.Attestant?.id ===
              props.message._Associations?.Attestant?.id
          )?._Associations?.Attestant
        : undefined,
    [
      formContext?.data?._Associations?.Rader,
      isMessageSentByAttestant,
      props.message._Associations?.Attestant?.id,
    ]
  );

  const reservForUser = useMemo(() => {
    const today = new Date();
    const type = formContext?.data?.TYP;
    if (!isMessageSentByAttestant) {
      return undefined;
    }

    return formContext?.data?._Associations?.Rader?.reduce<
      BokforderQuery_node_TEDEKBokforder__Associations_Rader | undefined
    >((prevRow, currentRow) => {
      if (
        currentRow._Associations?.Reserv?.id ===
          props.message._Associations?.Attestant?.id &&
        (prevRow?._Associations?.Reserv == null || prevRow === undefined)
      ) {
        return currentRow;
      } else if (
        currentRow._Associations?.Attestant?._Associations?.Franvaro_All?.some(
          franvaro =>
            isUserReservForFranvaro(
              franvaro,
              props.message._Associations?.Attestant?.id,
              type,
              today
            )
        ) &&
        !prevRow?._Associations?.Attestant?._Associations?.Franvaro_All?.some(
          franvaro =>
            isUserReservForFranvaro(
              franvaro,
              props.message._Associations?.Attestant?.id,
              type,
              today
            )
        )
      ) {
        return currentRow;
      }

      return prevRow;
    }, undefined)?._Associations?.Attestant;
  }, [
    formContext?.data?.TYP,
    formContext?.data?._Associations?.Rader,
    isMessageSentByAttestant,
    props.message._Associations?.Attestant?.id,
  ]);

  return (
    <EDBox
      sx={{
        alignItems: 'center',
        borderBottom: `1px solid ${AvenyTheme.palette.divider} `,
        display: 'flex',
        flexFlow: 'row nowrap',
        margin: `auto`,
        padding: `${AvenyTheme.spacing(0.5)} 0`,
        paddingRight: AvenyTheme.spacing(1.25),
        width: `calc(100% - ${AvenyTheme.spacing(1.25)})`,
        wordBreak: 'break-word',
        height: 'auto',
        '&:last-of-type': {
          borderBottom: 'none',
        },
      }}
      testID={`message-${props.index}-container`}
    >
      <EditableAttribute
        onFocus={(event: React.FocusEvent<HTMLElement>) => {
          if (event.target instanceof HTMLInputElement) {
            event.target.select();
          }
        }}
        autoFocus
        attribute={bokforderMessageAttribute}
        collection={bokforderMessageModel}
        disabled={!props.isEditMode}
        disablePaddingRight
        editMode={props.isEditMode}
        marker={props.isEditMode && props.hasChanged}
        endAdornment={
          <EDIconButton
            icon="delete"
            iconSize="medium"
            margin="none"
            onClick={props.deleteOrUndoMessage}
            padding="dense"
            tooltip={props.deleteOrUndoMessageTooltip}
          />
        }
        fullWidth
        label={getMessageLabel(
          props.message,
          isMessageSentByAttestant,
          attestantSender,
          reservForUser
        )}
        multiline
        onChange={value => {
          bokforderContext.addOrUpdateMessage(
            value,
            props.message.id,
            props.message._version ?? 1
          );
        }}
        size="large"
        value={props.message.MEDDELANDE}
        valueNewLine
        testId={`message-text-${props.index}`}
      />
      {props.showEditButtons &&
      !props.hasMessageProp &&
      formContext?.formState.editMode ? (
        <EDIconButton
          icon="edit"
          iconSize="medium"
          margin="none"
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            bokforderContext.setEditMessageID(props.message.id);
            props.setOriginalValue(props.message.MEDDELANDE ?? '');
          }}
          padding="dense"
          tooltip="Redigera meddelandet"
        />
      ) : undefined}
      {props.showEditButtons &&
      !props.hasMessageProp &&
      formContext?.formState.editMode ? (
        <EDIconButton
          icon="delete"
          iconSize="medium"
          margin="none"
          onClick={() => {
            bokforderContext.deleteMessage(props.message.id);
          }}
          padding="dense"
          tooltip="Ta bort meddelandet"
        />
      ) : undefined}
    </EDBox>
  );
};

export default BokforderMessage;
